// src/components/invoices/InvoicePage.jsx
import React, { useState, useEffect } from 'react';
import {
  Search, Download,
  Check, AlertCircle, FileText, X, Filter,
  RefreshCw
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import InvoiceForm from './InvoiceForm';
import invoiceService from '../../services/invoiceService';
import ExchangeRateWidget from '../public/ExchangeRateWidget';

const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  return (
    <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md max-w-md z-50 flex items-center justify-between ${
      type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 
      type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' : 
      'bg-blue-100 text-blue-800 border border-blue-200'
    }`}>
      <div className="flex items-center">
        {type === 'success' && <Check className="h-5 w-5 mr-2" />}
        {type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
        <p>{message}</p>
      </div>
      <button onClick={onClose} className="ml-4 text-gray-500 hover:text-gray-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

const InvoicePage = () => {
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isInvoiceFormOpen, setIsInvoiceFormOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [filters, setFilters] = useState({
    company: '', service_type: '', date_from: '', date_to: '', 
    status: '', payment_type: '', paid: ''
  });
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { fetchInvoices(); }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 5000);
  };

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      const response = await invoiceService.getList(filters);
      setInvoices(response.results || []);
      setStats(response.stats || {});
      setCurrentPage(1);
    } catch (error) {
      showNotification(t('fetchInvoicesError'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Submit search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvoices();
  };

  // Reset filters
  const handleReset = () => {
    setFilters({
      company: '',
      service_type: '',
      date_from: '',
      date_to: '',
      status: '',
      payment_type: '',
      paid: ''
    });
    fetchInvoices({});
  };

  // View invoice details
  const handleViewInvoice = async (id) => {
    try {
      const invoice = await invoiceService.getById(id);
      setCurrentInvoice(invoice);
      setFormTitle(t('viewInvoice'));
      setIsInvoiceFormOpen(true);
    } catch (error) {
      console.error(`Failed to get invoice ${id}:`, error);
      showNotification(t('fetchInvoiceError'), 'error');
    }
  };

  // Export to Excel
  const handleExportToExcel = () => {
    if (invoices.length === 0) {
      showNotification(t('noInvoicesToExport') || 'No invoices to export', 'error');
      return;
    }

    // Create HTML table for Excel
    let tableHTML = '<table border="1">';
    
    // Add headers
    tableHTML += '<thead><tr>';
    const headers = [
      'N', 'Invoice Number', 'Company', 'Date', 'Service Type', 
      'Total Amount', 'Status', 'Payment', 'Paid'
    ];
    headers.forEach(header => {
      tableHTML += `<th>${header}</th>`;
    });
    tableHTML += '</tr></thead>';
    
    // Add data rows
    tableHTML += '<tbody>';
    invoices.forEach((invoice, index) => {
      tableHTML += '<tr>';
      tableHTML += `<td>${index + 1}</td>`;
      tableHTML += `<td>${invoice.invoice_number}</td>`;
      tableHTML += `<td>${invoice.company}</td>`;
      tableHTML += `<td>${new Date(invoice.date).toLocaleDateString()}</td>`;
      tableHTML += `<td>${t(invoice.service_type)}</td>`;
      tableHTML += `<td>${invoice.total_amount}</td>`;
      tableHTML += `<td>${t(invoice.status)}</td>`;
      tableHTML += `<td>${invoice.payment_type || '-'}</td>`;
      tableHTML += `<td>${invoice.paid ? t('paid') : t('unpaid')}</td>`;
      tableHTML += '</tr>';
    });
    tableHTML += '</tbody></table>';

    // Create blob with proper Excel format
    const blob = new Blob([tableHTML], { 
      type: 'application/vnd.ms-excel' 
    });
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `invoices_${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);
    showNotification(t('exportSuccess') || 'Export successful', 'success');
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="container mx-auto px-2 sm:px-4 md:px-6 xl:px-8 py-3 sm:py-4 md:py-6">
      <Notification message={notification.message} type={notification.type} 
        onClose={() => setNotification({ message: '', type: '' })} />
      
      {/* RESPONSIVE PAGE HEADER */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-lg sm:text-xl md:text-2xl xl:text-3xl font-bold text-gray-800">{t('invoices')}</h1>
          
          <div className="flex flex-wrap gap-1 sm:gap-2">
            <button
              onClick={handleExportToExcel}
              className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center text-xs sm:text-sm"
              disabled={isLoading || invoices.length === 0}
            >
              <Download className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('exportExcel')}</span>
              <span className="sm:hidden">Excel</span>
            </button>
            
            <button
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              className={`px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-md flex items-center text-xs sm:text-sm ${
                isFilterExpanded 
                  ? 'bg-primary-100 text-primary-800' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('filters')}</span>
              <span className="sm:hidden">Filter</span>
            </button>
            
            <button
              onClick={fetchInvoices}
              className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-xs sm:text-sm"
              disabled={isLoading}
            >
              <RefreshCw className={`mr-1 h-3 w-3 sm:h-4 sm:w-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t('refresh')}</span>
              <span className="sm:hidden">↻</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* RESPONSIVE STATS CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 xl:gap-6 mb-4 md:mb-6">
        {/* Paid Total (Active) */}
        <div className="bg-white p-3 sm:p-4 xl:p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500 mb-1">{t('activePaidTotal')}</p>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold text-green-600">
                {new Intl.NumberFormat('en-US', { 
                  style: 'currency', 
                  currency: 'AMD' 
                }).format(stats?.paid_total || 0)}
              </p>
            </div>
            <div className="p-2 sm:p-3 xl:p-4 bg-green-100 rounded-full">
              <Check className="h-4 w-4 sm:h-5 sm:w-5 xl:h-6 xl:w-6 text-green-600" />
            </div>
          </div>
        </div>
        
        {/* Unpaid Total (Active) */}
        <div className="bg-white p-3 sm:p-4 xl:p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500 mb-1">{t('activeUnpaidTotal')}</p>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold text-red-600">
                {new Intl.NumberFormat('en-US', { 
                  style: 'currency', 
                  currency: 'AMD' 
                }).format(stats?.unpaid_total || 0)}
              </p>
            </div>
            <div className="p-2 sm:p-3 xl:p-4 bg-red-100 rounded-full">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 xl:h-6 xl:w-6 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-3 sm:p-4 xl:p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500 mb-1">{t('totalInvoices')}</p>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold text-blue-600">{stats?.total_count || 0}</p>
            </div>
            <div className="p-2 sm:p-3 xl:p-4 bg-blue-100 rounded-full">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 xl:h-6 xl:w-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
          <ExchangeRateWidget />
        </div>
      </div>
      
      {/* RESPONSIVE SERVICE TYPE QUANTITY TOTALS */}
      <div className="mb-2">
        <h3 className="text-sm sm:text-base md:text-lg xl:text-xl font-medium text-gray-800">
          {t('paidServiceQuantities')}
        </h3> 
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-5 gap-2 sm:gap-4 xl:gap-6 mb-4 md:mb-6">
        {['transitCard', 'truckInsurance', 'trailerInsurance', 'parking_40', 'parking_80'].map((service) => (
          <div key={service} className="bg-white p-2 sm:p-3 xl:p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="flex flex-col">
              <div className="mb-2">
                <p className="text-xs sm:text-sm text-gray-500 leading-tight">{t(service)}</p>
                <p className="text-sm sm:text-base xl:text-lg font-bold text-blue-600">
                  {stats?.service_quantities?.[service] || 0}
                </p>
              </div>
              <div className="p-1.5 sm:p-2 bg-blue-100 rounded-full flex items-center justify-center self-end">
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 xl:h-5 xl:w-5 text-blue-600" />
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* RESPONSIVE FILTERS SECTION */}
      {isFilterExpanded && (
        <div className="bg-white p-3 sm:p-4 xl:p-6 rounded-lg shadow-sm mb-4 md:mb-6 transition-all duration-300">
          <form onSubmit={handleSearch} className="space-y-4">
            <h3 className="text-sm sm:text-base md:text-lg font-medium text-gray-800 mb-3 flex items-center">
              <Filter className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-primary-600" />
              {t('searchFilters')}
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('serviceType')}</label>
                <select
                  name="service_type"
                  value={filters.service_type}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="transitCard">{t('transitCard')}</option>
                  <option value="truckInsurance">{t('truckInsurance')}</option>
                  <option value="trailerInsurance">{t('trailerInsurance')}</option>
                  <option value="parking_40">{t('parking_40')}</option>
                  <option value="parking_80">{t('parking_80')}</option>
                  <option value="other">{t('other')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dateFrom')}</label>
                <input
                  type="date"
                  name="date_from"
                  value={filters.date_from}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dateTo')}</label>
                <input
                  type="date"
                  name="date_to"
                  value={filters.date_to}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('status')}</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="pending">{t('pending')}</option>
                  <option value="processing">{t('processing')}</option>
                  <option value="completed">{t('completed')}</option>
                  <option value="cancelled">{t('cancelled')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('paymentStatus')}</label>
                <select
                  name="paid"
                  value={filters.paid}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="true">{t('paid')}</option>
                  <option value="false">{t('unpaid')}</option>
                </select>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
              <button
                type="button"
                onClick={handleReset}
                className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm"
              >
                {t('reset')}
              </button>
              
              <button
                type="submit"
                className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center justify-center text-sm"
              >
                <Search className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                {t('search')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* RESPONSIVE INVOICES TABLE */}
      {(() => {
        const totalPages = Math.ceil(invoices.length / pageSize);
        const paged = invoices.slice((currentPage - 1) * pageSize, currentPage * pageSize);
        return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-2 sm:p-3 xl:p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm sm:text-base xl:text-lg font-medium text-gray-800">{t('invoicesList')}</h3>
        </div>
        
        {/* Mobile Card View - Hidden on Tablet+ */}
        <div className="block md:hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="flex justify-center items-center">
                <svg className="animate-spin h-5 w-5 text-primary-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm">{t('loading')}</span>
              </div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-sm">{t('noInvoicesFound')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {paged.map((invoice) => (
                <div key={invoice.id} onClick={() => handleViewInvoice(invoice.id)}
                  className="p-3 hover:bg-gray-50 cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-primary-600 text-sm">{invoice.invoice_number}</p>
                      <p className="text-xs text-gray-500">{invoice.company}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">{t('date')}: </span>
                      <span>{new Date(invoice.date).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('serviceType')}: </span>
                      <span>{t(invoice.service_type)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('totalAmount')}: </span>
                      <span className="font-medium">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'AMD' }).format(invoice.total_amount)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('status')}: </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(invoice.status)}`}>
                        {t(invoice.status)}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">{t('payment')}: </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${invoice.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {invoice.paid ? t('paid') : t('unpaid')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Tablet+ Table View - Hidden on Mobile */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('invoiceNumber')}
                </th>
                <th scope="col" className="px-3 xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('company')}
                </th>
                <th scope="col" className="px-3 xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('date')}
                </th>
                <th scope="col" className="px-3 xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('serviceType')}
                </th>
                <th scope="col" className="px-3 xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('totalAmount')}
                </th>
                <th scope="col" className="px-3 xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('status')}
                </th>
                <th scope="col" className="px-3 xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('payment')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-5 w-5 text-primary-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{t('loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p>{t('noInvoicesFound')}</p>
                    <p className="text-sm text-gray-400 mt-1">{t('tryChangingFilters')}</p>
                  </td>
                </tr>
              ) : (
                paged.map((invoice) => (
                  <tr key={invoice.id} onClick={() => handleViewInvoice(invoice.id)}
                    className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-3 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-sm font-medium text-primary-600">
                      {invoice.invoice_number}
                    </td>
                    <td className="px-3 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-sm text-gray-800">
                      {invoice.company}
                    </td>
                    <td className="px-3 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(invoice.date).toLocaleDateString()}
                    </td>
                    <td className="px-3 xl:px-6 py-3 xl:py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {t(invoice.service_type)}
                      </span>
                    </td>
                    <td className="px-3 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'AMD' }).format(invoice.total_amount)}
                    </td>
                    <td className="px-3 xl:px-6 py-3 xl:py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(invoice.status)}`}>
                        {t(invoice.status)}
                      </span>
                    </td>
                    <td className="px-3 xl:px-6 py-3 xl:py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${invoice.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {invoice.paid ? t('paid') : t('unpaid')}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && invoices.length > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('show')}:</span>
              {[20, 50, 100].map(n => (
                <button key={n} onClick={() => { setPageSize(n); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs rounded ${pageSize === n ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {n}
                </button>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, invoices.length)} / {invoices.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => p === '…'
                  ? <span key={`e${idx}`} className="px-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded border ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}
      </div>
        );
      })()}

      {/* Invoice Form Modal */}
      <InvoiceForm
        isOpen={isInvoiceFormOpen}
        onClose={() => setIsInvoiceFormOpen(false)}
        onSubmit={() => {}}
        invoice={currentInvoice}
        title={formTitle}
        isViewMode={true}
        isLoading={false}
        showNotification={showNotification}
      />
    </div>
  );
};

export default InvoicePage;